import { Account } from "@unleashed/models/Account";
import { AccountParticipant } from "../features/account/models/AccountParticipant";
import {
  Product,
  Discount,
  CartItem,
  BaseProduct,
  PrePurchasedItem,
  AdhocDiscountViewModel,
  BookingItemViewModel,
  BundleConfig,
  BundleItemViewModel,
  ComboConfig,
  ComboItemViewModel,
  PersonViewModel,
  ProductDiscount,
  PurchaseItemViewModel,
  RedeemItemViewModel,
  TicketUpgradeDataModel,
  UpgradeTicketViewModel,
} from "../models";
import { mergeSimilarComboItemConfig } from "./Combo";
import {
  sortDataWithTicketsOnTop,
  mergeSimilarProducts,
} from "./DataFlattener";
import {
  getTotalPriceForItem,
  getProductIdOrTicketIdFromProduct,
} from "./Product";
import {
  filterUpgradedRedeemingTickets,
  findSimilarUpgradedTicket,
} from "./TicketUpgrade";
import { applyProductDiscount } from "./Discount";
import { ItemFeeViewModel } from "../models/ItemFeeViewModel";
import { ProductFee } from "../models/ProductFee";
import { Payment } from "../features/checkout/payment/models/Payment";
import { PaymentMethod } from "../enums/PaymentMethod";

export const convertProductToCartItem = (
  product: Product,
  quantity: number,
  discount?: Discount,
  isFromCheckInTab?: boolean,
  quantityFromCheckIn?: number
): CartItem => {
  return {
    billingInterval: product.billingInterval,
    contractDuration: product.contractDuration,
    productId: product.productId,
    ticketId: product.ticketId,
    bundleId: product.bundleId,
    bookingEvent: product.bookingEvent,
    bookingId: product.bookingId,
    uuid: product.uuid,
    name: product.name || "",
    isForKds: product.isForKds || false,
    price: product.price || 0,
    type: product.type || "",
    color: product.color,
    productLevelId: product.productLevelId,
    cafeCategoryId: product.cafeCategoryId,
    cafeCategoryName: product.cafeCategoryName,
    cafeCategoryDisplayOrder: product.cafeCategoryDisplayOrder,
    cafeCategoryIsActive: product.cafeCategoryIsActive,
    quantity,
    discount,
    isFromCheckInTab,
    isPrePurchase: false,
    quantityFromCheckIn: quantity === 0 ? 0 : quantityFromCheckIn || 0,
    productOptions: product.productOptions,
    isCombo: product.isCombo,
    comboConfigs: product.comboConfigs,
    isBundle: product.isBundle,
    bundleConfigs: product.bundleConfigs,
    isUpgradedCartTicket: product.isUpgradedCartTicket,
    itemFee: product.itemFee,
    productFee: product.productFee
  };
};

export const convertCartItemToProduct = (product: CartItem): BaseProduct => {
  return {
    billingInterval: product.billingInterval,
    contractDuration: product.contractDuration,
    productId: product.productId,
    ticketId: product.ticketId,
    bundleId: product.bundleId,
    bookingEvent: product.bookingEvent,
    bookingId: product.bookingId,
    uuid: product.uuid,
    name: product.name || "",
    isForKds: product.isForKds || false,
    type: product.type || "",
    color: product.color,
    quantity: product.quantity || 0,
    isPrePurchase: false,
    discount: product.discount,
    productLevelId: product.productLevelId,
    cafeCategoryId: product.cafeCategoryId,
    cafeCategoryName: product.cafeCategoryName,
    cafeCategoryDisplayOrder: product.cafeCategoryDisplayOrder,
    cafeCategoryIsActive: product.cafeCategoryIsActive,
    productOptions: product.productOptions,
    isCombo: product.isCombo,
    comboConfigs: product.comboConfigs,
    isBundle: product.isBundle,
    bundleConfigs: product.bundleConfigs,
  };
};

export const convertPrePurchasedItemToProduct = (
  product: PrePurchasedItem,
  accountHolderId?: number
): BaseProduct => {
  return {
    productId: product.productId,
    ticketId: product.ticketId,
    bookingEvent: product.bookingEvent,
    bookingId: product.bookingId,
    name: product.name || "",
    isForKds: product.isForKds || false,
    type: product.type || "",
    color: product.color,
    quantity: product.quantity || 0,
    isPrePurchase: true,
    accountHolderId: accountHolderId ?? product.accountHolderId,
    productLevelId: product.productLevelId,
    cafeCategoryId: product.cafeCategoryId,
    cafeCategoryName: product.cafeCategoryName,
    cafeCategoryDisplayOrder: product.cafeCategoryDisplayOrder,
    cafeCategoryIsActive: product.cafeCategoryIsActive,
    productOptions: product.productOptions,
  };
};

export const convertPrePurchasedItemToCartItem = (
  product: PrePurchasedItem
): CartItem => {
  return {
    productId: product.productId,
    ticketId: product.ticketId,
    bookingEvent: product.bookingEvent,
    bookingId: product.bookingId,
    name: product.name || "",
    isForKds: product.isForKds || false,
    price: undefined,
    quantity: product.quantity || 0,
    type: product.type || "",
    color: product.color,
    productLevelId: product.productLevelId,
    cafeCategoryId: product.cafeCategoryId,
    cafeCategoryName: product.cafeCategoryName,
    cafeCategoryDisplayOrder: product.cafeCategoryDisplayOrder,
    cafeCategoryIsActive: product.cafeCategoryIsActive,
    isPrePurchase: false,
    productOptions: product.productOptions,
    discount: product.discount,
    isUpgradedCartTicket: product.isUpgradedCartTicket,
  };
};

export const convertDataToCartItemFlattened = (
  data: (CartItem | PrePurchasedItem)[]
): CartItem[] => {
  const allFlattenedData: CartItem[] = [];
  sortDataWithTicketsOnTop(mergeSimilarProducts(data)).flatMap(
    (item: CartItem | PrePurchasedItem) => {
      allFlattenedData.push({
        ...item,
      });
    }
  );

  return allFlattenedData;
};

export const convertCartItemsToPurchaseItemViewModel = (
  itemsInCart: CartItem[] = [],
  discounts?: AdhocDiscountViewModel[],
  productsDiscount?: ProductDiscount[]
): PurchaseItemViewModel[] => {
  return itemsInCart
    .filter((r) => !r.isBookingEvent)
    .flatMap((cartItem: CartItem) => {
      const {
        productId,
        ticketId,
        price,
        name,
        color,
        type,
        quantity,
        productOptions,
        isVariablePrice,
        variablePrice,
        itemFee,
        productFee
      } = cartItem;

      const commonProperties = {
        productId,
        ticketId,
        pricePerUnit: (isVariablePrice ? variablePrice : price) || 0,
        name,
        color,
        type,
        productOptions,
      };

      return {
        ...commonProperties,
        quantity: quantity || 0,
        totalPrice: getTotalPriceForItem(cartItem),
        itemFee: itemFee,
        productFee,
        adhocDiscount:
          productsDiscount &&
          applyProductDiscount(cartItem, productsDiscount, discounts),
      };
    });
};

export const convertRedeemedItemsToRedeemItemViewModel = (
  selectedAccount: AccountParticipant,
  itemsForRedemption: PrePurchasedItem[],
  ticketUpgradeData: TicketUpgradeDataModel[],
  discounts?: AdhocDiscountViewModel[],
  productsDiscount?: ProductDiscount[]
): RedeemItemViewModel[] => {
  return mergeSimilarProducts(
    filterUpgradedRedeemingTickets(itemsForRedemption, ticketUpgradeData)
  ).map((redeemedItem: PrePurchasedItem) => ({
    accountId: selectedAccount?.accountId,
    productId: redeemedItem.productId,
    ticketId: redeemedItem.ticketId,
    quantity: redeemedItem.quantity,
    name: redeemedItem.name,
    productOptions: redeemedItem.productOptions,
    adhocDiscount:
      productsDiscount &&
      applyProductDiscount(redeemedItem, productsDiscount, discounts),
  }));
};

export const convertCartItemsToUpgradeTicketViewModel = (
  upgradedTicketsInCart: CartItem[] = [],
  ticketUpgradeData: TicketUpgradeDataModel[] = [],
  discounts?: AdhocDiscountViewModel[],
  productsDiscount?: ProductDiscount[]
): UpgradeTicketViewModel[] => {
  return ticketUpgradeData.map((data: TicketUpgradeDataModel) => ({
    upgradedTicketId: data.upgradedTicketId,
    previouslyAssignedTicketId: data.ticketId,
    totalPrice: data.upgradedTicketPrice,
    adhocDiscount:
      productsDiscount &&
      applyProductDiscount(
        upgradedTicketsInCart.find((x) => x.uuid === data.uuid) ??
          ({} as CartItem),
        productsDiscount,
        discounts
      ),
    isUpgradingPrePurchasedTicket: !data.isAdmitted,
  }));
};

export const convertComboItemsToPurchaseItemViewModel = (
  comboConfig: ComboConfig[]
): PurchaseItemViewModel[] => {
  return mergeSimilarComboItemConfig(comboConfig).flatMap(
    (comboItem: ComboConfig) => {
      if (
        !comboItem.comboItemProducts ||
        comboItem.comboItemProducts.length === 0
      )
        return [];

      const { productId, name, price, productOptions } =
        comboItem.comboItemProducts[0];

      const commonProperties = {
        productId,
        name,
        pricePerUnit: price || 0,
        productOptions,
        quantity: comboItem.quantity || 0,
      };

      return {
        ...commonProperties,
      };
    }
  );
};

export const convertBundleItemsToPurchaseItemViewModel = (
  bundleConfig?: BundleConfig
): PurchaseItemViewModel[] => {
  if (!bundleConfig) return [];

  return [
    ...(bundleConfig.bundleTickets ?? []),
    ...(bundleConfig.bundleProducts ?? []),
  ].flatMap((bundleItem: Product) => {
    if (!bundleItem) return [];

    const { productId, ticketId, name, price } = bundleItem;

    const commonProperties = {
      productId,
      ticketId,
      name,
      pricePerUnit: price || 0,
      quantity: bundleItem.quantity || 0,
    };

    return {
      ...commonProperties,
    };
  });
};

export const convertTicketToUpgradeTicketViewModel = (
  cartItem: CartItem,
  ticketUpgradeData: TicketUpgradeDataModel[]
): UpgradeTicketViewModel => {
  const ticketData = findSimilarUpgradedTicket(
    ticketUpgradeData,
    cartItem
  ) as TicketUpgradeDataModel;
  return {
    upgradedTicketId: ticketData?.upgradedTicketId,
    previouslyAssignedTicketId: ticketData?.ticketId,
    totalPrice: ticketData?.upgradedTicketPrice,
  };
};

export const convertCartItemsToComboItemViewModel = (
  itemsInCart: CartItem[] = [],
  discounts?: AdhocDiscountViewModel[],
  productsDiscount?: ProductDiscount[]
): ComboItemViewModel[] => {
  return itemsInCart.flatMap((cartItem: CartItem) => {
    const { productId, price, name, type, quantity, comboConfigs } = cartItem;

    const commonProperties = {
      productId,
      pricePerUnit: price || 0,
      name,
      type,
    };

    return {
      ...commonProperties,
      quantity: quantity || 0,
      totalPrice: getTotalPriceForItem(cartItem),
      adhocDiscount:
        productsDiscount &&
        applyProductDiscount(cartItem, productsDiscount, discounts),
      purchaseItems: convertComboItemsToPurchaseItemViewModel(
        comboConfigs ?? []
      ),
    };
  });
};

export const convertCartItemsToBundleItemViewModel = (
  itemsInCart: CartItem[] = [],
  discounts?: AdhocDiscountViewModel[],
  productsDiscount?: ProductDiscount[]
): BundleItemViewModel[] => {
  return itemsInCart.flatMap((cartItem: CartItem) => {
    const { bundleId, price, name, type, quantity, bundleConfigs } = cartItem;

    const commonProperties = {
      bundleId,
      pricePerUnit: price || 0,
      name,
      type,
    };

    return {
      ...commonProperties,
      quantity: quantity || 0,
      totalPrice: getTotalPriceForItem(cartItem),
      adhocDiscount:
        productsDiscount &&
        applyProductDiscount(cartItem, productsDiscount, discounts),
      purchaseItems: convertBundleItemsToPurchaseItemViewModel(bundleConfigs),
    };
  });
};

export const convertCartItemsToFeeItemViewModel = (
  itemsInCart: CartItem[] = [],
  discounts?: AdhocDiscountViewModel[],
  productsDiscount?: ProductDiscount[]
): ItemFeeViewModel[]  => {

  return itemsInCart.flatMap((cartItem: CartItem) => {
    const { itemFeeId, price, name, quantity } = cartItem;
    return { 
      id:itemFeeId,
      amount: price,
      name:name,
      quantity:quantity,
      adhocDiscount:
        productsDiscount &&
        applyProductDiscount(cartItem, productsDiscount, discounts),
    };
  });
};

export const convertCartItemsToProductFeeItemViewModel = (
  productFeesInCart: CartItem[] = [],
): ProductFee[]  => {

  return productFeesInCart.flatMap((cartItem: CartItem) => {
    const { productFeeId, price, name, quantity } = cartItem;
    return { 
      id:productFeeId,
      amount: price,
      description:name,
      quantity:quantity,
    };
  });
};

export const convertCartItemsToProductFeeViewModelToCreateInvoice = (itemsInCart: CartItem[] = [],
  discountReasons?: AdhocDiscountViewModel[]) : ProductFee[] => {

  return itemsInCart.flatMap<ProductFee>((cartItem: CartItem) => {
    const { productFeeId, price, name, quantity}  = cartItem;

    return {
      amount: price,
      id:productFeeId,
      quantity: quantity || 0,
      description: name,
      adhocDiscount: convertDiscountAdhocDiscountViewModelToCreateInvoice(
        cartItem.discount,
        discountReasons
      ),
    };
  });
};

export const convertCartItemsToItemFeeViewModelToCreateInvoice = (itemsInCart: CartItem[] = [],
  discountReasons?: AdhocDiscountViewModel[]) : ItemFeeViewModel[] => {

  return itemsInCart.flatMap<ItemFeeViewModel>((cartItem: CartItem) => {
    const { itemFeeId, price, name, quantity}  = cartItem;

    return {
      amount: price,
      id:itemFeeId,
      quantity: quantity || 0,
      name: name,
      adhocDiscount: convertDiscountAdhocDiscountViewModelToCreateInvoice(
        cartItem.discount,
        discountReasons
      ),
    };
  });
};

export const convertCartItemsToBookingItemViewModel = (
  cartItem?: CartItem,
  discounts?: AdhocDiscountViewModel[],
  productsDiscount?: ProductDiscount[]
): BookingItemViewModel | undefined => {
  if (cartItem) {
    const { bookingId, price, name, type, quantity } = cartItem;

    const commonProperties = {
      bookingId,
      name,
      type,
    };

    return {
      ...commonProperties,
      quantity: quantity || 0,
      totalPrice: price,
      adhocDiscount:
        productsDiscount &&
        applyProductDiscount(cartItem, productsDiscount, discounts),
    };
  }

  return undefined;
};

export const convertAccountToPersonViewModel = (
  checkInParticipants: Account[]
) => {
  return checkInParticipants.map((participant: Account): PersonViewModel => {
    return {
      id: participant.id,
      firstName: participant.firstName,
      lastName: participant.lastName,
    };
  });
};

export const convertCartItemsToBookingItemViewModelToCreateInvoice = (
  cartItem?: CartItem,
  discountReasons?: AdhocDiscountViewModel[]
): BookingItemViewModel | undefined => {
  if (cartItem) {
    const { price, name, type, quantity, bookingId } = cartItem;

    const commonProperties = {
      bookingId,
      name,
      type,
    };

    return {
      ...commonProperties,
      quantity: quantity || 0,
      totalPrice: price,
      adhocDiscount: convertDiscountAdhocDiscountViewModelToCreateInvoice(
        cartItem.discount,
        discountReasons
      ),
    };
  }
  return undefined;
};

export const convertCartItemsToPurchaseItemViewModelToCreateInvoice = (
  itemsInCart: CartItem[] = [],
  discountReasons?: AdhocDiscountViewModel[]
): PurchaseItemViewModel[] => {
  return itemsInCart
    .filter((r) => !r.isBookingEvent)
    .flatMap((cartItem: CartItem) => {
      const {
        productId,
        ticketId,
        price,
        name,
        color,
        type,
        quantity,
        productOptions,
        isVariablePrice,
        variablePrice,
      } = cartItem;

      const commonProperties = {
        productId,
        ticketId,
        pricePerUnit: (isVariablePrice ? variablePrice : price) || 0,
        name,
        color,
        type,
        productOptions,
      };

      return {
        ...commonProperties,
        quantity: quantity || 0,
        totalPrice: getTotalPriceForItem(cartItem),
        adhocDiscount: convertDiscountAdhocDiscountViewModelToCreateInvoice(
          cartItem.discount,
          discountReasons
        ),
      };
    });
};

export const convertRedeemedItemsToRedeemItemViewModelToCreateInvoice = (
  itemsForRedemption: PrePurchasedItem[],
  ticketUpgradeData: TicketUpgradeDataModel[],
  discountReasons?: AdhocDiscountViewModel[]
): PurchaseItemViewModel[] => {
  return mergeSimilarProducts(
    filterUpgradedRedeemingTickets(itemsForRedemption, ticketUpgradeData)
  ).flatMap((redeemItem: PrePurchasedItem) => {
    const {
      productId,
      ticketId,
      name,
      quantity,
      productOptions,
      accountHolderId,
    } = redeemItem;

    const commonProperties = {
      productId,
      ticketId,
      name,
      productOptions,
      accountId: accountHolderId,
    };

    return {
      ...commonProperties,
      quantity: quantity || 0,
      adhocDiscount: convertDiscountAdhocDiscountViewModelToCreateInvoice(
        redeemItem.discount,
        discountReasons
      ),
    };
  });
};

export const convertCartItemsToComboItemViewModelToCreateInvoice = (
  itemsInCart: CartItem[] = [],
  discountReasons?: AdhocDiscountViewModel[]
): ComboItemViewModel[] => {
  return itemsInCart.flatMap((cartItem: CartItem) => {
    const { productId, price, name, type, quantity, comboConfigs } = cartItem;

    const commonProperties = {
      productId,
      pricePerUnit: price || 0,
      name,
      type,
    };

    return {
      ...commonProperties,
      quantity: quantity || 0,
      totalPrice: getTotalPriceForItem(cartItem),
      adhocDiscount: convertDiscountAdhocDiscountViewModelToCreateInvoice(
        cartItem.discount,
        discountReasons
      ),
      purchaseItems: convertComboItemsToPurchaseItemViewModel(
        comboConfigs ?? []
      ),
    };
  });
};

export const convertCartItemsToBundleItemViewModelToCreateInvoice = (
  itemsInCart: CartItem[] = [],
  discountReasons?: AdhocDiscountViewModel[]
): BundleItemViewModel[] => {
  return itemsInCart.flatMap((cartItem: CartItem) => {
    const { bundleId, price, name, type, quantity, bundleConfigs } = cartItem;

    const commonProperties = {
      bundleId,
      pricePerUnit: price || 0,
      name,
      type,
    };

    return {
      ...commonProperties,
      quantity: quantity || 0,
      totalPrice: getTotalPriceForItem(cartItem),
      adhocDiscount: convertDiscountAdhocDiscountViewModelToCreateInvoice(
        cartItem.discount,
        discountReasons
      ),
      purchaseItems: convertBundleItemsToPurchaseItemViewModel(bundleConfigs),
    };
  });
};

export const convertCartItemsToUpgradeTicketsViewModelToCreateInvoice = (
  itemsInCart: CartItem[] = [],
  ticketUpgradeData: TicketUpgradeDataModel[],
  discountReasons?: AdhocDiscountViewModel[]
): UpgradeTicketViewModel[] => {
  return ticketUpgradeData.map((data: TicketUpgradeDataModel) => ({
    upgradedTicketId: data.upgradedTicketId,
    previouslyAssignedTicketId: data.ticketId,
    totalPrice: data.upgradedTicketPrice,
    adhocDiscount: convertDiscountAdhocDiscountViewModelToCreateInvoice(
      (itemsInCart.find((x) => x.uuid === data.uuid) ?? ({} as CartItem))
        .discount,
      discountReasons
    ),
    isUpgradingPrePurchasedTicket: !data.isAdmitted,
  }));
};

export const convertDiscountAdhocDiscountViewModelToCreateInvoice = (
  discount?: Discount,
  discountReasons?: AdhocDiscountViewModel[]
) => {
  if (discount) {
    const discountReasonId =
      discountReasons?.find(
        (x: AdhocDiscountViewModel) =>
          x.discountReasonCode === discount?.discountReasonCode
      )?.discountReasonId ?? 0;

    return {
      discountAmount: Math.abs(discount.amount),
      discountReasonCode: discount.discountReasonCode,
      discountReasonId,
      discountType: discount.type,
      discountValue: Math.abs(discount.value),
    };
  }

  return undefined;
};

export const convertItemsToOrderItem = (itemsForKds: CartItem[]) => {
  return itemsForKds.flatMap((item: CartItem) => {
    // Non-combo, for KDS
    if (!item.isCombo) {
      return [
        {
          productId: getProductIdOrTicketIdFromProduct(item),
          quantity: item.quantity,
          name: item.name,
          productOptions: item.productOptions,
        },
      ];
    }

    // Combo item, for KDS
    if (item.isCombo && item.comboConfigs?.length) {
      const comboItems = [];

      for (const comboConfig of item.comboConfigs) {
        const products =
          comboConfig.comboItemProducts?.filter((p: Product) => p.isForKds) ||
          [];

        for (const comboItem of products) {
          comboItems.push({
            productId: getProductIdOrTicketIdFromProduct(comboItem),
            quantity: (comboConfig.quantity ?? 1) * item.quantity,
            name: comboItem.name,
            productOptions: comboItem.productOptions,
          });
        }
      }

      return [
        {
          productId: getProductIdOrTicketIdFromProduct(item),
          quantity: item.quantity,
          name: item.name,
          productOptions: undefined, // combos do not have product options
          comboItems,
        },
      ];
    }

    return [];
  });
};

export const convertPaymentsToPrintWithRemainingBalance = (
  payments: Payment[],
): Payment[] => {
  const mutablePayments = payments.map((payment) => ({ ...payment }));
  const totusGroups = new Map<string, Payment[]>();

  mutablePayments.forEach((payment) => {
    if (
      payment.paymentMethod === PaymentMethod.TotusGiftCard &&
      payment.lastFour
    ) {
      const group = totusGroups.get(payment.lastFour) || [];
      group.push(payment);
      totusGroups.set(payment.lastFour, group);
    }
  });

  totusGroups.forEach((paymentsInGroup) => {
    if (paymentsInGroup.length <= 1) return;

    const latestPayment = paymentsInGroup.reduce((latest, current) =>
      current.paymentSequenceId > latest.paymentSequenceId ? current : latest,
    );

    paymentsInGroup.forEach((payment) => {
      if (payment !== latestPayment) {
        payment.newBalance = undefined;
      }
    });
  });

  return mutablePayments.map(({ newBalance, ...rest }) => ({
    ...rest,
    ...(newBalance !== undefined && { remainingBalance: newBalance }),
  }));
};
